import { Link } from "react-router-dom";
import { Copy, Check, ArrowRight, Scales, Plus, CheckCircle } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import { useCompare } from "../context/CompareContext";
import { 
  getProviderName, 
  getPrimarySpecialty, 
  getLocation, 
  getSpecialtyColor,
  getNameSlug 
} from "../lib/api";

const ProviderCard = ({ provider }) => {
  const [copied, setCopied] = useState(false);
  const { addToCompare, removeFromCompare, isInCompare, canAddMore } = useCompare();
  
  const npi = provider?.number || "";
  const name = getProviderName(provider);
  const specialty = getPrimarySpecialty(provider);
  const location = getLocation(provider);
  const entityType = provider?.enumeration_type === "NPI-1" ? "Individual" : "Organization";
  const specialtyClass = getSpecialtyColor(specialty);
  const nameSlug = getNameSlug(name);
  const inCompare = isInCompare(npi);

  const handleCopyNpi = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(npi);
      setCopied(true);
      toast.success("NPI copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy NPI");
    }
  };

  const handleCompareToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inCompare) {
      removeFromCompare(npi);
      toast.info("Removed from comparison");
    } else if (canAddMore) {
      addToCompare(provider);
      toast.success("Added to comparison");
    } else {
      toast.error("Maximum 3 providers can be compared");
    }
  };

  return (
    <div 
      className={`provider-card bg-white border border-slate-200 rounded-lg p-6 border-l-4 ${specialtyClass} ${inCompare ? 'ring-2 ring-teal' : ''}`}
      data-testid={`provider-card-${npi}`}
    >
      <div className="flex flex-col h-full">
        {/* Top row: Entity badge + Compare button */}
        <div className="flex items-center justify-between mb-3">
          <span className={`entity-badge ${entityType === 'Individual' ? 'individual' : 'organization'}`}>
            {entityType}
          </span>
          
          <button
            onClick={handleCompareToggle}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all ${
              inCompare 
                ? 'bg-teal text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-teal/10 hover:text-teal'
            }`}
            title={inCompare ? "Remove from compare" : "Add to compare"}
            data-testid={`compare-toggle-${npi}`}
          >
            {inCompare ? (
              <>
                <CheckCircle className="w-3.5 h-3.5" weight="fill" />
                <span>Comparing</span>
              </>
            ) : (
              <>
                <Scales className="w-3.5 h-3.5" />
                <span>Compare</span>
              </>
            )}
          </button>
        </div>

        {/* Provider name */}
        <h3 className="font-display font-semibold text-lg text-slate-900 mb-1 line-clamp-2">
          {name || "Unknown Provider"}
        </h3>

        {/* Specialty */}
        <p className="text-sm text-teal font-medium mb-2">
          {specialty || "Specialty not listed"}
        </p>

        {/* Location */}
        <p className="text-sm text-slate-500 mb-4">
          {location || "Location not available"}
        </p>

        {/* NPI with copy button */}
        <div className="flex items-center gap-2 mb-4 mt-auto">
          <span className="text-xs text-slate-400 uppercase tracking-wide">NPI</span>
          <span className="npi-badge text-sm bg-slate-100 text-slate-700 px-2 py-1 rounded">
            {npi}
          </span>
          <button
            onClick={handleCopyNpi}
            className="p-1.5 text-slate-400 hover:text-teal transition-colors rounded-md hover:bg-slate-100"
            title={`Copy NPI ${npi} to clipboard`}
            aria-label={`Copy NPI ${npi} to clipboard`}
            data-testid={`copy-npi-button-${npi}`}
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-500 copy-success" weight="bold" aria-hidden="true" />
            ) : (
              <Copy className="w-4 h-4" aria-hidden="true" />
            )}
          </button>
        </div>

        {/* View profile button */}
        <Link
          to={`/provider/${npi}/${nameSlug}`}
          className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-slate-50 hover:bg-navy hover:text-white text-slate-700 font-medium rounded-lg transition-all text-sm group"
          data-testid={`view-profile-button-${npi}`}
          title={
            name
              ? `View ${name}'s NPI profile${specialty ? ` — ${specialty}` : ''}${location ? ` in ${location}` : ''}`
              : 'View provider NPI profile'
          }
          aria-label={
            name
              ? `View ${name}'s profile${specialty ? ` — ${specialty}` : ''}${location ? ` in ${location}` : ''}`
              : 'View provider profile'
          }
        >
          View Profile
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
};

export default ProviderCard;
